import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * GA4 (Google Analytics 4) — page views for one or more properties.
 * Production: set GA_MEASUREMENT_ID in deployment (comma-separated for multiple, e.g. yourshirt.co + www).
 * Local dev: set REACT_APP_GA_MEASUREMENT_ID in .env (comma-separated allowed).
 */

export function getMeasurementIds() {
  let raw =
    (typeof window !== "undefined" && window.__ENV__?.REACT_APP_GA_MEASUREMENT_ID) ||
    process.env.REACT_APP_GA_MEASUREMENT_ID ||
    "";
  return raw
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

export default function GoogleAnalytics() {
  const location = useLocation();
  const measurementIds = getMeasurementIds();

  useEffect(() => {
    if (measurementIds.length === 0) return;

    const firstId = measurementIds[0];

    // Load gtag script once (use first ID for script src)
    if (!window.gtag) {
      const script = document.createElement("script");
      script.src = `https://www.googletagmanager.com/gtag/js?id=${firstId}`;
      script.async = true;
      document.head.appendChild(script);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag("js", new Date());
      measurementIds.forEach((id) => {
        window.gtag("config", id, { send_page_view: false });
      });
    }

    measurementIds.forEach((id) => {
      window.gtag("event", "page_view", {
        send_to: id,
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    });
  }, [measurementIds.join(","), location.pathname, location.search]);

  return null;
}

/**
 * Fire GA4 purchase event. Use same transaction_id as backend for deduplication.
 * @param {string} transactionId - Stripe payment_intent_id
 * @param {number} value - Order total amount
 * @param {string} currency - e.g. "USD"
 */
export function trackPurchase(transactionId, value, currency) {
  const ids = getMeasurementIds();
  if (ids.length === 0 || !window.gtag) return;
  ids.forEach((id) => {
    window.gtag("event", "purchase", {
      send_to: id,
      transaction_id: transactionId,
      value,
      currency,
    });
  });
}
